import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { useEffect, useRef, useState } from 'react';
import { useMutationState } from '~/hooks/reactQuery/useMutationState';
import { useStoreActions } from '~/store/store-context';
import { UploadBulkJobType, UploadBulkType } from '~/types/api/billing/bulk/upload-csv';
import { ServerResponseError } from '../BulkBillingModal.utils';
const readFileAsText = (file)=>{
    return new Promise((resolve, reject)=>{
        const reader = new FileReader();
        reader.onload = (event)=>{
            var _event_target;
            resolve({
                name: file.name,
                content: (_event_target = event.target) === null || _event_target === void 0 ? void 0 : _event_target.result
            });
        };
        reader.onerror = ()=>{
            reject(new Error('Failed to read file'));
        };
        reader.readAsText(file);
    });
};
export const useUploadBulkBilling = ()=>{
    const isJobInProgress = useRef(false);
    const { api, logBI } = useStoreActions();
    const [fileDetails, setFileDetails] = useState(undefined);
    const [progress, setProgress] = useState(undefined);
    const [bulkUploadId, setBulkUploadId] = useState(undefined);
    const { mutate, isLoading, data, reset, error } = useMutationState({
        mutationFn: async (file)=>{
            const details = await readFileAsText(file);
            setFileDetails(details);
            logBI(18702);
            const response = await api.billing.bulk.upload(details.content);
            if (response.type === UploadBulkType.Invalid) {
                logBI(18703);
            } else if (response.type === UploadBulkType.Valid) {
                isJobInProgress.current = true;
                setBulkUploadId(response.valid.bulkUploadId);
                while(isJobInProgress.current){
                    const jobResponse = await api.billing.bulk.uploadResult(response.valid.bulkUploadId);
                    if (jobResponse.validationResultType === UploadBulkJobType.InProgress) {
                        setProgress({
                            completed: jobResponse.validationResultInProgress.lineItemsCompletedCount,
                            total: jobResponse.validationResultInProgress.totalLineItemsCount
                        });
                    } else {
                        setProgress(undefined);
                        isJobInProgress.current = false;
                        if (jobResponse.validationResultType === UploadBulkJobType.Valid) {
                            setFileDetails(_object_spread_props(_object_spread({}, details), {
                                lines: jobResponse.validationResultValid.lineItemsCount,
                                invoicesCount: jobResponse.validationResultValid.invoicesCount,
                                agreements: undefined,
                                invoices: jobResponse.validationResultValid.invoices
                            }));
                        } else {
                            logBI(18703);
                            return {
                                type: UploadBulkType.Invalid,
                                invalid: {
                                    rowErrors: jobResponse.validationResultInvalid.errors
                                }
                            };
                        }
                    }
                }
            }
            return response;
        },
        onError: ()=>{
            logBI(18703);
        }
    });
    const resetValidation = ()=>{
        reset();
        setProgress(undefined);
        setFileDetails(undefined);
        setBulkUploadId(undefined);
    };
    useEffect(()=>{
        return ()=>{
            isJobInProgress.current = false;
        };
    }, []);
    let validationErrors;
    if ((data === null || data === void 0 ? void 0 : data.type) === UploadBulkType.Invalid) {
        validationErrors = data === null || data === void 0 ? void 0 : data.invalid;
    } else if (error) {
        validationErrors = {
            globalErrors: [
                {
                    code: ServerResponseError.SERVER_VALIDATION_UNKNOWN_ERROR,
                    message: ''
                }
            ],
            rowErrors: []
        };
    }
    return {
        uploadCsv: mutate,
        fileDetails,
        bulkUploadId,
        progress,
        isLoading: isLoading,
        validationErrors,
        wasFileSelected: !!fileDetails,
        reset: resetValidation
    };
};
